import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import OutlineTextButton from 'LuxKit/Button/OutlineTextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import BusinessTravellerRequestDemoModal from 'businessTraveller/components/demo-form/BusinessTravellerRequestDemoModal'
import { getImageUrl } from 'lib/image/imageUtils'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE } from 'components/App/Routes/routeConstants'

const SectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(56)};
  padding-bottom: ${rem(48)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(80)};
    padding-bottom: ${rem(80)};
  }
`

const StepsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(28)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
  }
`

const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: stretch;
  }
`

const StepImageBox = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  ${mediaQueryUp.desktop} {
    flex: 0 0 50%;
  }
`

const StepImage = styled.img`
  width: 100%;
  height: auto;
`

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: ${rem(24)};
  gap: ${rem(16)};
  background: rgba(243, 243, 244, 0.50);

  ${mediaQueryUp.desktop} {
    padding: ${rem(40)} ${rem(24)};
    gap: ${rem(0)};

    > :nth-child(2) {
      margin-top: ${rem(24)};
    }

    > :nth-child(3) {
      margin-top: ${rem(12)};
    }
  }
`

const SectionHeading = styled(Heading)`
  font-weight: 400;
`

const MobileBreak = styled.br`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const StepTitle = styled(Heading)`
  font-weight: 450;

  ${mediaQueryUp.desktop} {
    font-size: 24px;
    line-height: 28px;
    font-weight: 500;
  }
`

const STEPS = [
  {
    imageId: 'step-1-v2',
    step: 'STEP 1',
    title: 'Find the right options at the right price',
    description: 'Give your team access to a wide range of travel options across flights, accommodation and car hire, with exclusive corporate rates negotiated by Luxury Escapes.',
  },
  {
    imageId: 'step-2',
    step: 'STEP 2',
    title: 'Ensure compliance in real time',
    description: 'Admins set policies, spend limits and approval rules once. Travellers then see what\'s in and out of budget upfront, helping them make compliant choices and reduce costs.',
  },
  {
    imageId: 'step-3-v2',
    step: 'STEP 3',
    title: 'Reward smarter travel choices',
    description: 'Encourage travellers to book under budget by sharing a portion of the savings as personal Luxury Escapes credit. Travellers can also earn loyalty points, while businesses link programs like Qantas Business Rewards.',
  },
]

function BusinessTravellerStepsSection() {
  const showModal = useContext(ModalContext)
  const openDemoModal = useCallback(() => {
    showModal(<BusinessTravellerRequestDemoModal />)
  }, [showModal])

  return <SectionContainer>
    <Group direction="vertical" gap={40} desktopGap={48} desktopHorizontalAlign="center">
      <SectionHeading variant="heading2" align="start" desktopAlign="center">
        How Business Traveller<MobileBreak /> <i>reduces travel spend</i>
      </SectionHeading>

      <StepsGrid>
        {STEPS.map(step => (
          <StepCard key={step.step}>
            <StepImageBox>
              <StepImage
                src={getImageUrl(step.imageId)}
                alt={step.title}
              />
            </StepImageBox>
            <StepContent>
              <BodyText variant="medium">{step.step}</BodyText>
              <StepTitle variant="heading3">{step.title}</StepTitle>
              <BodyText variant="large">{step.description}</BodyText>
            </StepContent>
          </StepCard>
        ))}
      </StepsGrid>

      <Group direction="vertical" gap={16} tabletDirection="horizontal" tabletHorizontalAlign="center">
        <TextButton kind="primary" size="medium" fit="mobile-full-width" to={BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE}>
          Start saving today
        </TextButton>
        <OutlineTextButton kind="secondary" size="medium" onClick={openDemoModal} fit="mobile-full-width">
          Book a free demo
        </OutlineTextButton>
      </Group>
    </Group>
  </SectionContainer>
}

export default BusinessTravellerStepsSection
